import React, { useState, useCallback, useEffect, useMemo } from 'react';
import OrderListInspector, { customerName } from '../components/orders/OrderListInspector';
import OrderEditPage from '../components/orders/OrderEditPage';
import ShipmentDispatchDrawer, { getShippingCharged } from '../components/orders/ShipmentDispatchDrawer';
import ShippedDetailDrawer from '../components/orders/ShippedDetailDrawer';
import { useToast } from '../components/ToastManager';
import useSalesChannels from '../hooks/useSalesChannels';
import {
  normalizeSalesChannel, getPaymentLabel,
  STATUS_DEFS, statusDef, fmtMoney, formatOrderDate,
} from '../utils/orderUtils';
import { exportAllOrdersTab, exportShippingPending, exportShippingShipped } from '../utils/exportOrders';
import { paramsListaPedidos } from '../utils/pedidosSearchParams';
import PageHeader from '../components/ui/PageHeader';
import HeaderUtilities from '../components/ui/HeaderUtilities';
import EspejoFranja from '../components/EspejoFranja';
import ModuleTabs from '../components/ui/ModuleTabs';
import SearchToolbar from '../components/ui/SearchToolbar';
import ActiveFilterChips from '../components/ui/ActiveFilterChips';
import DataTable from '../components/ui/DataTable';
import StatusBadge from '../components/ui/StatusBadge';
import ChannelBadge from '../components/ui/ChannelBadge';
import Button from '../components/ui/Button';
import DateRangeFilter, { resolvePreset, DATE_PRESETS } from '../components/ui/DateRangeFilter';
import FilterChips from '../components/ui/FilterChips';
import './OrdersPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PER_PAGE = 30;

// ── Constants ──────────────────────────────────────────────────────────────────

/* Estados Woo — labels de siempre + variante visual del StatusBadge */
/* Mapa estados Woo → label visible (renombrados con Miguel, F3 hallazgo 4):
   processing = pagado y AÚN NO despachado → "Por despachar" (ojo: no es
   idéntico al conteo del badge de Gestión de envíos, que cuenta por
   shipping_lines + estado_envio; este cuenta por estado Woo).
   on-hold = pago reportado sin confirmar — es EL MISMO estado que los tokens
   llaman "en espera" (variant en-espera); el label visible es uno solo. */

const PAYMENT_OPTIONS = [
  'Efectivo', 'Transferencia', 'PSE', 'Mercado Pago', 'Bold', 'Nequi', 'Daviplata',
];

const SHIPPING_SUB_TABS = [
  { id: 'pending', label: 'Pendientes de envío' },
  { id: 'shipped', label: 'Ya enviados' },
];

// Chips de canal a partir de la lista dinámica de canales (incluye eventos y archivados,
// para poder filtrar ventas históricas). El valor del chip es el NOMBRE del canal.
function channelChipIcon(ch) {
  const n = (ch.name || '').toLowerCase();
  if (n === 'whatsapp') return '💬 ';
  if (n === 'tienda física') return '🏪 ';
  if (n === 'web') return '🌐 ';
  if (ch.type === 'event' || n === 'feria') return '🎪 ';
  return '🏷️ ';
}
function channelsToChips(channels) {
  return [
    { value: 'all', label: 'Todos los canales' },
    ...channels.map((c) => ({ value: c.name, label: `${channelChipIcon(c)}${c.name}` })),
  ];
}

// ── AllOrdersTab — Pestaña 1 (migrada al kit, F3) ─────────────────────────────

function AllOrdersTab({ user, onEdit, onManageShipping, focusRecord = null, onRegisterActions = null, soloLectura = false }) {
  const { showToast } = useToast();
  const isVendedor = user?.role === 'vendedor';

  /* Filtros de SERVIDOR (recargan la consulta): estado, rango, búsqueda.
     Aplican de inmediato (brief §7 — la consulta es liviana: una página de 30);
     el botón "Aplicar filtros" del diseño anterior se retiró. */
  const [status, setStatus] = useState(null);          // null = todos
  const [rango, setRango]   = useState(() => ({ preset: '14d', ...resolvePreset('14d') }));
  const [searchInput, setSearchInput]     = useState('');
  const [searchApplied, setSearchApplied] = useState('');

  /* Filtros de CLIENTE (sobre lo ya cargado): canal y métodos de pago */
  const [channel, setChannel]   = useState('all');
  const [payments, setPayments] = useState([]);

  const [orders, setOrders]           = useState([]);
  // W3: frescura del espejo de pedidos (viaja en cada respuesta de /api/orders).
  const [espejoInfo, setEspejoInfo]   = useState(null);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError]     = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [selected, setSelected]       = useState(null);

  /* Cancelar (dentro de OrderListInspector) avisa acá: el padre posee la lista y
     `selected`, y los pasa a 'cancelled' — misma conducta que el handleCancel
     viejo (F5 Tema 3, extracción verbatim). */
  const handleOrderCancelled = (id) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: 'cancelled' } : o)));
    setSelected((s) => (s && s.id === id ? { ...s, status: 'cancelled' } : s));
  };

  // Cambio de estado no-destructivo desde el selector del drawer: el hijo ya confirmó contra
  // WooCommerce; acá solo se refleja en la lista y el `selected` (el backend limpió su caché).
  // P2: al editar el contacto de un pedido, reemplaza ese pedido en la lista y en el seleccionado
  // con el que devolvió el backend (billing/shipping/meta al día), sin recargar la página.
  const handleContactoGuardado = (updated) => {
    if (!updated || updated.id == null) return;
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    setSelected((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
  };
  const handleOrderStatusChanged = (id, newStatus) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o)));
    setSelected((s) => (s && s.id === id ? { ...s, status: newStatus } : s));
  };

  // Búsqueda con debounce (el search va al servidor/Woo)
  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(searchInput.trim()), 450);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Conteos globales de estados relevantes (sin filtro de fecha) — los tres de
  // siempre + processing (F3 hallazgo 4: "Por despachar" con su conteo)
  useEffect(() => {
    ['processing', 'on-hold', 'cancelled', 'refunded'].forEach(async (st) => {
      try {
        const res = await fetch(`${API_URL}/api/orders?status=${st}&per_page=1&page=1`);
        if (!res.ok) return;
        const data = await res.json();
        setStatusCounts((prev) => ({ ...prev, [st]: data.total ?? 0 }));
      } catch { /* silencioso */ }
    });
  }, []);

  // buscar-historial (28-sep): con texto en el buscador, la búsqueda va a TODO el historial del
  // espejo (se ignora el período); sin texto, la ventana rápida de siempre. La regla vive en
  // utils/pedidosSearchParams (pura, testeada) — acá solo se consume.
  const buildParams = useCallback(
    (pg) => paramsListaPedidos({ perPage: PER_PAGE, page: pg, status, rango, search: searchApplied }).toString(),
    [status, rango, searchApplied],
  );

  // `fresh` salta la caché de 4 min del backend (nocache=1) — SOLO para el botón «Actualizar»
  // explícito. Las cargas implícitas (cambio de filtro, paginación, montaje) siguen usando la
  // caché, que existe por performance: el refresh explícito es lo único que el usuario aprieta
  // cuando cambió algo FUERA de la app (wp-admin) y necesita verlo ya (Miguel 2026-08-25).
  const loadOrders = useCallback(async ({ pg = 1, append = false, fresh = false } = {}) => {
    if (pg === 1) { setLoading(true); setLoadError(null); }
    else setLoadingMore(true);
    try {
      const res = await fetch(`${API_URL}/api/orders?${buildParams(pg)}${fresh ? '&nocache=1' : ''}`);
      if (!res.ok) throw new Error('Error al cargar pedidos.');
      const data = await res.json();
      setTotal(data.total || 0);
      setPage(pg);
      setOrders((prev) => append ? [...prev, ...(data.orders || [])] : (data.orders || []));
      setEspejoInfo({ hace: data.espejo_actualizado_hace_seg ?? null, advertencia: data.espejo_advertencia || null, vacio: data.espejo_vacio === true, migrando: data.espejo_migrando === true });
    } catch (err) {
      if (pg === 1) setLoadError(err.message);
      else showToast({ message: err.message, variant: 'error' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams, showToast]);

  // Recarga cuando cambia cualquier filtro de servidor (aplicación inmediata)
  useEffect(() => { setSelected(null); loadOrders({ pg: 1 }); }, [loadOrders]);

  /* Deep-link del buscador global (F3): abre el pedido seleccionado en el
     inspector. Si no está en la página cargada (rango/filtros distintos), se
     trae solo por id — el inspector no depende de que la fila exista en la
     lista (sin fila resaltada ni prev/next en ese caso, a propósito). */
  useEffect(() => {
    if (!focusRecord?.id) return undefined;
    const enLista = orders.find((o) => o.id === focusRecord.id);
    if (enLista) { setSelected(enLista); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/${focusRecord.id}`);
        if (!res.ok) throw new Error('No se pudo abrir el pedido.');
        const o = await res.json();
        if (!cancelled) setSelected(o);
      } catch (err) {
        if (!cancelled) showToast({ message: err.message, variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [focusRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  const { channels: channelList } = useSalesChannels({ includeArchived: true });
  const channelChips = useMemo(() => channelsToChips(channelList), [channelList]);

  // Filtrado en cliente: canal y método de pago (igual que antes)
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (channel !== 'all') {
      result = result.filter((o) => normalizeSalesChannel(o).key === channel);
    }
    if (payments.length > 0) {
      result = result.filter((o) => {
        const pm = getPaymentLabel(o).label?.toLowerCase() || '';
        return payments.some((m) => pm.includes(m.toLowerCase()));
      });
    }
    return result;
  }, [orders, channel, payments]);

  // Chips de filtros activos (el rango custom ya tiene su chip en DateRangeFilter)
  const activeChips = useMemo(() => {
    const chips = [];
    if (status) chips.push({ id: 'status', label: `Estado: ${statusDef(status).label}` });
    if (channel !== 'all') {
      const lbl = channelChips.find((c) => c.value === channel)?.label;
      if (lbl) chips.push({ id: 'channel', label: lbl });
    }
    payments.forEach((m) => chips.push({ id: `pm_${m}`, label: `Pago: ${m}` }));
    // Con búsqueda activa el período no aplica: su chip no se muestra (diría un filtro que
    // no está filtrando — el selector ya dice «Todo el historial»).
    if (!searchApplied && rango.preset !== '14d' && rango.preset !== 'custom') {
      const lbl = DATE_PRESETS.find(([id]) => id === rango.preset)?.[1];
      if (lbl) chips.push({ id: 'rango', label: lbl });
    }
    if (searchApplied) chips.push({ id: 'search', label: `"${searchApplied}"` });
    return chips;
  }, [status, channel, payments, rango.preset, searchApplied, channelChips]);

  const removeChip = (id) => {
    if (id === 'status') setStatus(null);
    else if (id === 'channel') setChannel('all');
    else if (id.startsWith('pm_')) setPayments((p) => p.filter((m) => `pm_${m}` !== id));
    else if (id === 'rango') setRango({ preset: '14d', ...resolvePreset('14d') });
    else if (id === 'search') { setSearchInput(''); setSearchApplied(''); }
  };

  const clearAll = () => {
    setStatus(null); setChannel('all'); setPayments([]);
    setRango({ preset: '14d', ...resolvePreset('14d') });
    setSearchInput(''); setSearchApplied('');
  };

  const esDefault = rango.preset === '14d' && !status && channel === 'all'
    && payments.length === 0 && !searchApplied;

  const exportar = useCallback(() => {
    const label = esDefault ? 'Ultimas2semanas'
      : rango.from && rango.to ? `${rango.from}_${rango.to}`
      : status ? statusDef(status).label
      : 'Todos';
    exportAllOrdersTab(filteredOrders, { filterLabel: label });
  }, [esDefault, rango.from, rango.to, status, filteredOrders]);

  /* Acciones del header (F3 hallazgo 5): Actualizar y Exportar viven arriba a
     la derecha, en la fila del título — este effect se las registra al padre.
     Al desmontar (cambiar de tab) se des-registran. */
  useEffect(() => {
    onRegisterActions?.({
      loading,
      canExport: filteredOrders.length > 0,
      onRefresh: () => loadOrders({ pg: 1, fresh: true }),
      onExport: exportar,
    });
    return () => onRegisterActions?.(null);
  }, [onRegisterActions, loading, filteredOrders.length, loadOrders, exportar]);

  const hasMore = orders.length < total && !loading;

  const statusChipOptions = STATUS_DEFS.map((s) => ({
    id: s.value,
    label: s.label,
    count: statusCounts[s.value] > 0 ? statusCounts[s.value] : undefined,
  }));

  return (
    <>
      {/* W3: frescura de los pedidos sincronizados — informativa + CTA si está vacío */}
      <EspejoFranja
        espejoInfo={espejoInfo}
        sujeto="pedidos"
        onMigrarEspejo={async () => {
          const r = await fetch(`${API_URL}/api/espejo-pedidos/migracion`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
          if (!r.ok) throw new Error('No se pudo arrancar la sincronización');
          return r.json();
        }}
      />
      <SearchToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Cliente, # pedido, producto…"
        resultText={loading ? 'Cargando…' : esDefault
          ? `${filteredOrders.length} ${filteredOrders.length === 1 ? 'pedido' : 'pedidos'} · últimas 2 semanas`
          : `${filteredOrders.length} de ${total.toLocaleString('es-CO')} pedidos`}
        /* S1#4e: 183.22px = "500 pedidos · últimas 2 semanas". Manda la forma
           POR DEFECTO, no la filtrada: el sufijo de texto pesa más que los
           dígitos ("10000 de 100.000 pedidos" son 147.69px, bastante menos).
           Sin esto nacía en 62.22px ("Cargando…") y crecía 114px al llegar el
           dato — el movimiento que la tabla de cierre marcó 0/114/114.
           Techo medido: 1024 aguanta 196px, que es el ancho más ajustado. */
        countWidth={184}
      >
        <select value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Canal">
          {channelChips.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {/* Método de pago EN LA FILA (F4.5: el panel "Filtros" murió). El
           select AGREGA — cada elección se vuelve chip removible abajo, así
           el multiselección no se pierde. */}
        <select
          value=""
          onChange={(e) => {
            const m = e.target.value;
            if (m) setPayments((p) => (p.includes(m) ? p : [...p, m]));
          }}
          aria-label="Método de pago"
        >
          <option value="">{payments.length ? `Pago: ${payments.length} método${payments.length !== 1 ? 's' : ''}` : 'Método de pago'}</option>
          {PAYMENT_OPTIONS.filter((m) => !payments.includes(m)).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {/* buscador-tolerante: mientras hay texto el período NO aplica — el selector no puede
            decir «Últimas 2 semanas» sobre una lista que trae todo el historial. Al borrar,
            vuelve el filtro con el rango que estaba. */}
        {searchInput.trim() !== ''
          ? <span className="op-periodo-historial" title="Mientras buscas, el período no aplica: se busca en todo el historial">Todo el historial</span>
          : <DateRangeFilter value={rango} onChange={setRango} />}
      </SearchToolbar>

      {/* buscar-historial: mientras hay texto, la búsqueda ignora el período y va a TODO el
          historial del espejo. Al borrar el texto, vuelve la ventana rápida. Si hay MÁS
          resultados que los cargados (página de 30), la nota lo dice — el recorte nunca es
          silencioso; «Cargar más» pagina sobre el historial completo. */}
      {searchInput.trim() !== '' && (
        <div className="op-busqueda-historial">
          Buscando en todo el historial
          {searchApplied && !loading && total > orders.length
            ? ` · ${total.toLocaleString('es-CO')} resultados — se muestran los ${orders.length} más recientes (carga más o afina la búsqueda)`
            : ''}
        </div>
      )}

      {/* Estados con conteos: FILTRO (pills), no navegación */}
      <FilterChips
        options={statusChipOptions}
        value={status}
        onChange={setStatus}
        ariaLabel="Filtrar por estado"
      />

      <ActiveFilterChips chips={activeChips} onRemove={removeChip} onClearAll={clearAll} />

      <OrderListInspector
        orders={filteredOrders}
        selected={selected}
        onSelect={setSelected}
        loading={loading}
        error={loadError}
        onRetry={() => loadOrders({ pg: 1 })}
        pagination={{ mode: 'loadMore', hasMore, loadingMore, onLoadMore: () => loadOrders({ pg: page + 1, append: true }) }}
        empty={{ icon: '🛍️', title: 'No hay pedidos en este rango', description: 'Ajusta las fechas para ver más atrás.' }}
        hasActiveFilters={activeChips.length > 0}
        onClearFilters={clearAll}
        isVendedor={isVendedor}
        soloLectura={soloLectura}
        onEdit={onEdit}
        onManageShipping={onManageShipping}
        onOrderCancelled={handleOrderCancelled}
        onOrderStatusChanged={handleOrderStatusChanged}
        onContactoGuardado={handleContactoGuardado}
      />
    </>
  );
}

// ── ShippingTab — Pestaña 2 (CONSERVA — se re-estila en una pasada posterior) ──

function ShippingTab({ onShipmentConfirmed, registerControls = null, soloLectura = false }) {
  const { showToast } = useToast();
  const [subTab, setSubTab]         = useState('pending');
  const [channel, setChannel]       = useState('all');
  const [dateFrom, setDateFrom]     = useState('');   // vacío para pendientes
  const [dateTo, setDateTo]         = useState('');
  // Regla global de fechas (F3.3): DateRangeFilter también acá
  const [rangoShipped, setRangoShipped] = useState(() => ({ preset: '14d', ...resolvePreset('14d') }));
  const [orders, setOrders]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected]     = useState(null); // pedido abierto en el drawer

  const loadShipping = useCallback(async ({ tab = subTab, pg = 1, append = false, overrideDates } = {}) => {
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const endpoint = tab === 'shipped' ? 'shipping-history' : 'shipping-pending';
      const params   = new URLSearchParams({ page: pg, per_page: PER_PAGE });
      // Para "ya enviados" aplicar fechas; para "pendientes" no (mostrar todos)
      if (tab === 'shipped') {
        const df = overrideDates?.dateFrom !== undefined ? overrideDates.dateFrom : dateFrom;
        const dt = overrideDates?.dateTo   !== undefined ? overrideDates.dateTo   : dateTo;
        if (df) params.set('after',  `${df}T00:00:00-05:00`);
        if (dt) params.set('before', `${dt}T23:59:59-05:00`);
      }
      const res = await fetch(`${API_URL}/api/orders/${endpoint}?${params}`);
      if (!res.ok) throw new Error('Error al cargar envíos.');
      const data = await res.json();
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(pg);
      setOrders((prev) => append ? [...prev, ...(data.orders || [])] : (data.orders || []));
    } catch (err) {
      showToast({ message: err.message, variant: 'error' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [subTab, dateFrom, dateTo, showToast]);

  // Carga inicial: pendientes sin filtro de fecha
  useEffect(() => { loadShipping({ tab: 'pending', pg: 1 }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cambio de sub-tab con fechas apropiadas
  const handleSubTabChange = (tab) => {
    if (tab === subTab) return;
    setChannel('all');
    setSelected(null);
    setOrders([]);
    setTotal(0);
    setTotalPages(1);
    if (tab === 'shipped') {
      const def = { preset: '14d', ...resolvePreset('14d') };
      setRangoShipped(def);
      setDateFrom(def.from);
      setDateTo(def.to);
      setSubTab(tab);
      loadShipping({ tab, pg: 1, overrideDates: { dateFrom: def.from, dateTo: def.to } });
    } else {
      setDateFrom('');
      setDateTo('');
      setSubTab(tab);
      loadShipping({ tab, pg: 1, overrideDates: { dateFrom: '', dateTo: '' } });
    }
  };

  const { channels: channelListB } = useSalesChannels({ includeArchived: true });
  const channelChipsB = useMemo(() => channelsToChips(channelListB), [channelListB]);

  // Filtrado de canal en cliente
  const filteredOrders = useMemo(() => {
    if (channel === 'all') return orders;
    return orders.filter((o) => normalizeSalesChannel(o).key === channel);
  }, [orders, channel]);

  const hasMore = page < totalPages && !loading;

  // Label para export de enviados
  const shippedFilterLabel = (() => {
    if (rangoShipped.preset === '14d') return 'Ultimas2semanas';
    if (dateFrom && dateTo) return `${dateFrom}_${dateTo}`;
    return 'Todos';
  })();

  // Utilidades universales en el encabezado (F4.4): esta tab registra sus
  // controles al padre — Actualizar/Exportar dejan la toolbar (posición
  // divergente) y viven en el MISMO lugar que en "Todos los pedidos".
  const exportar = useCallback(() => {
    if (subTab === 'shipped') exportShippingShipped(filteredOrders, { filterLabel: shippedFilterLabel });
    else exportShippingPending(filteredOrders);
  }, [subTab, filteredOrders, shippedFilterLabel]);

  useEffect(() => {
    registerControls?.({
      loading,
      canExport: filteredOrders.length > 0,
      onRefresh: () => loadShipping({ pg: 1 }),
      onExport: exportar,
    });
    return () => registerControls?.(null);
  }, [registerControls, loading, filteredOrders.length, loadShipping, exportar]);

  // ↑/↓ del drawer: navegación continua sobre la lista filtrada (como Pedidos)
  const selectedIdx = selected ? filteredOrders.findIndex((o) => o.id === selected.id) : -1;
  const drawerPrev = selectedIdx > 0 ? () => setSelected(filteredOrders[selectedIdx - 1]) : null;
  const drawerNext = selectedIdx >= 0 && selectedIdx < filteredOrders.length - 1
    ? () => setSelected(filteredOrders[selectedIdx + 1]) : null;

  // Columnas del DataTable (molde). Pendientes: lo justo para decidir el despacho;
  // el formulario de captura vive en el drawer. Ya enviados: el costo real y su
  // diferencia a la vista, el resto (dirección, comentario) en el drawer.
  const pendingColumns = [
    { id: 'number',  header: 'Pedido', render: (o) => (
      <span className="op-cell-num"><strong>#{o.number}</strong>
        <span className="op-ship-ico op-ship-ico--pend" title="Envío pendiente" aria-label="Envío pendiente">📦</span>
      </span>
    ) },
    { id: 'fecha',   header: 'Fecha', hideSm: true, render: (o) => <span className="op-cell-nowrap">{formatOrderDate(o.date_created)}</span> },
    { id: 'cliente', header: 'Cliente', render: (o) => { const n = customerName(o); return <span className="op-cell-clip" title={n}>{n}</span>; } },
    { id: 'canal',   header: 'Canal', hideSm: true, render: (o) => <ChannelBadge channel={normalizeSalesChannel(o).key} /> },
    { id: 'total',   header: 'Total', align: 'right', render: (o) => fmtMoney(o.total) },
    { id: 'envio',   header: 'Cobrado envío', align: 'right', hideSm: true, render: (o) => fmtMoney(getShippingCharged(o)) },
    { id: 'estado',  header: 'Estado', render: (o) => { const d = statusDef(o.status); return <StatusBadge variant={d.variant}>{d.label}</StatusBadge>; } },
  ];

  const shippedColumns = [
    { id: 'number',  header: 'Pedido', render: (o) => (
      <span className="op-cell-num"><strong>#{o.number}</strong>
        <span className="op-ship-ico" title="Enviado" aria-label="Enviado">✅</span>
      </span>
    ) },
    { id: 'cliente', header: 'Cliente', hideSm: true, render: (o) => { const n = customerName(o); return <span className="op-cell-clip" title={n}>{n}</span>; } },
    { id: 'fechaenvio', header: 'Fecha envío', render: (o) => <span className="op-cell-nowrap">{o.fecha_envio || '—'}</span> },
    { id: 'transporte', header: 'Transportadora', render: (o) => {
      const nombre = o.nombre_transportista ? ` · ${o.nombre_transportista}` : '';
      return <span className="op-cell-clip" title={`${o.tipo_transporte === 'empresa' ? 'Empresa' : 'Mensajero'}${nombre}`}>{o.tipo_transporte === 'empresa' ? 'Empresa' : 'Mensajero'}{nombre}</span>;
    } },
    { id: 'costo',   header: 'Costo real', align: 'right', render: (o) => (o.costo_envio_real != null ? fmtMoney(o.costo_envio_real) : '—') },
    { id: 'dif',     header: 'Diferencia', align: 'right', hideSm: true, render: (o) => {
      const charged = o.envio_cobrado_cliente; const cost = o.costo_envio_real;
      if (typeof charged !== 'number' || typeof cost !== 'number') return '—';
      const d = charged - cost;
      if (d === 0) return '$0';
      return <span className={d > 0 ? 'shr-diff-gain' : 'shr-diff-loss'}>{`${fmtMoney(Math.abs(d))} ${d > 0 ? 'a favor' : 'asumida'}`}</span>;
    } },
    { id: 'pago',    header: 'Pago envío', hideSm: true, render: (o) => ({ efectivo: 'Efectivo', transferencia: 'Transferencia', pendiente: 'Pendiente' }[o.pago_envio_metodo] || o.pago_envio_metodo || '—') },
    { id: 'gestiono', header: 'Gestionó', hideSm: true, render: (o) => o.despachado_por || '—' },
  ];

  // "Ya enviados" arranca en 14 días (rendimiento: el historial escanea Woo).
  // Si el rango no trae nada, el vacío es ACLARATORIO + ofrece ampliar — no una
  // tabla muda. "Pendientes" no tiene fecha (muestra todos): su vacío es literal.
  const verTodoHistorial = () => {
    const def = { preset: 'all', ...resolvePreset('all') };
    setRangoShipped(def);
    setDateFrom(def.from || '');
    setDateTo(def.to || '');
    loadShipping({ pg: 1, overrideDates: { dateFrom: def.from || '', dateTo: def.to || '' } });
  };
  const emptyState = subTab === 'pending'
    ? { icon: '📦', title: 'No hay envíos pendientes.', description: 'Cuando un pedido con envío quede por despachar, aparece aquí.' }
    : rangoShipped.preset === 'all'
      ? { icon: '📦', title: 'No hay envíos registrados todavía.', description: 'Ningún pedido se ha despachado aún.' }
      : {
          icon: '📦',
          title: 'No hay envíos en este período.',
          description: 'Quizá los despachos son de antes del rango elegido.',
          action: <Button size="sm" variant="tertiary" onClick={verTodoHistorial}>Ver todo el historial</Button>,
        };

  return (
    <>
      {/* Sub-tabs — al kit (F3.5 punto 4: misma letra y diseño que el módulo) */}
      <ModuleTabs
        tabs={SHIPPING_SUB_TABS}
        active={subTab}
        onChange={handleSubTabChange}
        ariaLabel="Vistas de envíos"
      />

      {/* Toolbar al kit: canal (select, aplica al elegir), período (regla
         global de fechas) y acciones — el botón Aplicar murió */}
      <div className="ui-toolbar">
        <div className="ui-toolbar-cluster">
          <select value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Canal">
            {channelChipsB.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {subTab === 'shipped' && (
            <DateRangeFilter
              value={rangoShipped}
              allowAll
              onChange={(v) => {
                setRangoShipped(v);
                setDateFrom(v.from);
                setDateTo(v.to);
                loadShipping({ pg: 1, overrideDates: { dateFrom: v.from, dateTo: v.to } });
              }}
            />
          )}
        </div>
      </div>

      {/* Contador */}
      <div className="op-count">
        {loading ? 'Cargando…' : (() => {
          if (subTab === 'pending') {
            return total > 0
              ? `Mostrando ${filteredOrders.length} de ${total} pedidos pendientes de envío`
              : `${filteredOrders.length} ${filteredOrders.length === 1 ? 'pedido pendiente' : 'pedidos pendientes'} de envío`;
          }
          const suffix = rangoShipped.preset === '14d' ? ' de las últimas 2 semanas' : '';
          return `Mostrando ${filteredOrders.length} de ${total} envíos${suffix}`;
        })()}
      </div>

      {/* Lista al molde: DataTable + drawer (F5 Tema 4). El acordeón inline murió
         (resucitaba el docked que enterramos en Inventario); el flujo de despacho
         vive en el drawer compartido, igual que Retiros. */}
      <DataTable
        columns={subTab === 'shipped' ? shippedColumns : pendingColumns}
        rows={filteredOrders}
        rowKey="id"
        loading={loading}
        empty={emptyState}
        onRowClick={(o) => setSelected(o)}
        selectedKey={selected?.id ?? null}
        pagination={{ mode: 'loadMore', hasMore, loadingMore, onLoadMore: () => loadShipping({ pg: page + 1, append: true }) }}
      />

      {subTab === 'pending' ? (
        <ShipmentDispatchDrawer
          key={selected?.id ?? 'none'}
          order={selected}
          soloLectura={soloLectura}
          onClose={() => setSelected(null)}
          onPrev={drawerPrev}
          onNext={drawerNext}
          onConfirmed={() => { setSelected(null); loadShipping({ pg: 1 }); onShipmentConfirmed?.(); }}
        />
      ) : (
        <ShippedDetailDrawer
          key={selected?.id ?? 'none'}
          order={selected}
          soloLectura={soloLectura}
          onClose={() => setSelected(null)}
          onPrev={drawerPrev}
          onNext={drawerNext}
          onEdited={() => loadShipping({ pg: 1 })}
        />
      )}
    </>
  );
}

// ── OrderEditPage — LA única página de Pedidos (F3.5, dos superficies) ────────
// El inspector es la vista; esta página es solo EDICIÓN. La lista queda
// montada oculta mientras está abierta (filtros/scroll/página sobreviven).

// ── Componente principal ──────────────────────────────────────────────────────

export default function OrdersPage({ user, navTab = null, soloLectura = false }) {
  const [mainTab, setMainTab]           = useState('orders');
  const [fullPage, setFullPage]         = useState(null); // { order, edit } — página del pedido (F3.2)
  const [focusRecord, setFocusRecord]   = useState(null); // deep-link del buscador global (F3)
  // Navegación desde Hunter (pestaña) y desde el buscador global (registro).
  useEffect(() => {
    if (navTab?.tab && ['orders', 'shipping'].includes(navTab.tab)) setMainTab(navTab.tab);
    if (navTab?.recordId) {
      setMainTab('orders');
      setFullPage(null);
      setFocusRecord({ id: navTab.recordId, ts: navTab.ts });
    }
  }, [navTab]);
  const [pendingShipCount, setPendingShipCount] = useState(null);
  const [headerActions, setHeaderActions] = useState(null);     // registradas por AllOrdersTab (hallazgo 5)
  const [shippingActions, setShippingActions] = useState(null); // registradas por ShippingTab (F4.4)

  // Carga el conteo de envíos pendientes (silencioso, sin bloquear la UI)
  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/shipping-pending?per_page=1&page=1`);
      if (!res.ok) return;
      const data = await res.json();
      setPendingShipCount(data.total ?? 0);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { fetchPendingCount(); }, [fetchPendingCount]);

  const handleManageShipping = useCallback(() => {
    setFullPage(null);
    setMainTab('shipping');
  }, []);

  const handleShipmentConfirmed = useCallback(() => {
    setPendingShipCount((c) => (c != null ? Math.max(0, c - 1) : null));
  }, []);

  return (
    <>
    {/* La vista de lista queda MONTADA (oculta) mientras la página del pedido
       está abierta — filtros, orden, página y scroll sobreviven al volver. */}
    <div className="orders-page page-shell" style={fullPage ? { display: 'none' } : undefined}>
      <PageHeader
        title="Pedidos"
        description="Gestiona pagos, preparación y envíos."
        tabs={{
          tabs: [
            { id: 'orders', label: 'Todos los pedidos' },
            { id: 'shipping', label: 'Gestión de envíos', badge: pendingShipCount > 0 ? pendingShipCount : undefined },
          ],
          active: mainTab,
          onChange: setMainTab,
        }}
        utilities={(() => {
          /* Utilidades universales (F4.4): Actualizar → Exportar, MISMO lugar
             en las dos tabs — cada tab registra sus controles. */
          const ctl = mainTab === 'orders' ? headerActions : shippingActions;
          return ctl ? (
            <HeaderUtilities
              onRefresh={ctl.onRefresh}
              refreshing={ctl.loading}
              onExport={ctl.onExport}
              exportDisabled={!ctl.canExport}
            />
          ) : null;
        })()}
      />

      {/* Contenido */}
      <div className="op-body">
        {mainTab === 'orders' ? (
          <AllOrdersTab
            user={user}
            onEdit={(o) => setFullPage({ order: o })}
            onManageShipping={handleManageShipping}
            focusRecord={focusRecord}
            onRegisterActions={setHeaderActions}
            soloLectura={soloLectura}
          />
        ) : (
          <ShippingTab onShipmentConfirmed={handleShipmentConfirmed} registerControls={setShippingActions} soloLectura={soloLectura} />
        )}
      </div>
    </div>

    {/* La única página de Pedidos: la EDICIÓN (F3.5). OrderDetailModal sigue
       vivo para Caja y Resumen hasta que migren. */}
    {fullPage && (
      <OrderEditPage
        key={fullPage.order.id}
        order={fullPage.order}
        onBack={() => setFullPage(null)}
        onListChanged={() => headerActions?.onRefresh?.()}
      />
    )}
    </>
  );
}
